import { Flex, type TextProps, type FlexProps, rem, Header } from '@vimeo/bokeh';
import { ChevronRightSmall } from '@vimeo/bokeh-icons';

import { usePlaylistStyles } from '../../hooks/use-playlist';
export interface PlaylistHeaderProps extends FlexProps {
  header?: HeaderLinkProps;
}

/**
 * PlaylistHeader includes the leading text for the collection and optional children for additional actions,
 * such as navigation buttons, a switch for autoplay, or manage button.
 */
export const PlaylistHeader = ({ header, children, onClick, ...rest }: PlaylistHeaderProps) => {
  const { outerSpacing } = usePlaylistStyles();

  return (
    <Flex
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      gap="200"
      pt={outerSpacing}
      {...rest}
    >
      {header ? (
        <HeaderLink {...header} onClick={header.to ? onClick : undefined} />
      ) : (
        <Flex flex="1" />
      )}

      {children}
    </Flex>
  );
};

export interface HeaderLinkProps extends TextProps {
  /** the leading text for link, e.g "next up", "collection name" */
  text: string;
  /** url or uri link to other pages */
  to?: string;
  /** used for Pendo */
  headerLinkDataId?: string;
}

// NOTE: don't override focus styles yet to align with other wayfinder widgets
const HeaderLink = ({ text, to, headerLinkDataId, ...rest }: HeaderLinkProps) => {
  return (
    <Flex
      data-group
      data-id={headerLinkDataId}
      noOfLines={1}
      shrink="1"
      display="flex"
      {...(to && {
        as: 'a',
        href: to,
        _hover: { color: 'text-secondary', transition: 'all 0.1s ease-in-out', mr: '-0.25rem' },
        _focusVisible: { outlineColor: 'focus' },
      })}
      {...rest}
    >
      <Header noOfLines={1} size="md" color="text-primary" as="h2">
        {text}
      </Header>
      {to ? (
        <ChevronRightSmall
          width={rem(24)}
          height={rem(32)}
          verticalAlign="text-top"
          _groupHover={{ ml: '0.25rem', transition: 'margin 0.1s ease-in-out' }}
        />
      ) : null}
    </Flex>
  );
};
